import { api, get, post } from '@/lib/api';
import type { StatusDeProjeto } from '@/features/projetos/navegacao';

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

const del = (caminho: string) => api<unknown>(caminho, { method: 'DELETE' });

const BASE = '/projects';

// ── tipos ────────────────────────────────────────────────────────────────────

export interface LinhaDeProjeto {
  id: string;
  number: string;
  name: string;
  description: string | null;
  status: StatusDeProjeto;
  managerId: string | null;
  teamId: string | null;
  accountId: string | null;
  startAt: number | null;
  finishAt: number | null;
  createdAt: string;
  progressPercent: number;
  tasksDone: number;
  tasksTotal: number;
}

export interface TarefaDeProjeto {
  id: string;
  projectId: string;
  parentId: string | null;
  outlineNumber: string;
  depth: number;
  name: string;
  description: string | null;
  mode: 'AUTO' | 'MANUAL';
  durationMinutes: number;
  startAt: number | null;
  finishAt: number | null;
  isCritical: boolean;
  totalSlack: number;
  freeSlack: number;
  drivingLinkId: string | null;
  constraintType: 'ASAP' | 'ALAP' | 'SNET' | 'SNLT' | 'FNET' | 'FNLT' | 'MSO' | 'MFO';
  constraintDate: number | null;
  deadline: number | null;
  assigneeId: string | null;
  assignee: { id: string; name: string; displayName: string | null } | null;
  teamId: string | null;
  team: { id: string; name: string } | null;
  progress: number;
  boardSectionId: string | null;
  childCount: number;
  ticketId: string | null;
  isMilestone: boolean;
  recurrence?: string | null;
  recurrenceInterval?: number | null;
  predecessors: DependenciaDeTarefa[];
  successors: DependenciaDeTarefa[];
}

export interface ComentarioDeTarefa {
  id: string;
  taskId: string;
  authorId: string | null;
  author: { id: string; name: string; displayName: string | null } | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItem {
  id: string;
  taskId: string;
  body: string;
  done: boolean;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface TarefaMinhaAtividade {
  id: string;
  projectId: string;
  project: { id: string; number: string; name: string; status: string };
  name: string;
  outlineNumber: string;
  isCritical: boolean;
  progress: number;
  startAt: number | null;
  finishAt: number | null;
  teamId: string | null;
  team: { id: string; name: string } | null;
}

export interface DependenciaDeTarefa {
  id: string;
  predecessorId: string;
  successorId: string;
  kind: 'FS' | 'SS' | 'FF' | 'SF';
  lagMinutes: number;
  lagIsElapsed: boolean;
}

export interface ResultadoCronograma {
  tasks: Record<string, {
    id: string;
    start: number;
    finish: number;
    earlyStart: number;
    earlyFinish: number;
    lateStart: number;
    lateFinish: number;
    totalSlackMinutes: number;
    freeSlackMinutes: number;
    isCritical: boolean;
    drivingLinkId?: string;
  }>;
  projectStart: number;
  projectFinish: number;
  criticalPath: string[];
  conflicts: Array<{ kind: string; [key: string]: unknown }>;
  warnings: Array<{ kind: string; [key: string]: unknown }>;
  overallocations: unknown[];
  stats: { taskCount: number; linkCount: number; iterations: number; computeMs: number };
}

export interface MarcoDeProjeto {
  id: string;
  name: string;
  description: string | null;
  dueAt: number;
  completedAt: number | null;
}

export interface RiscoDeProjeto {
  id: string;
  title: string;
  description: string | null;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'OPEN' | 'MITIGATED' | 'ACCEPTED' | 'CLOSED';
  ownerId: string | null;
  mitigation: string | null;
}

export interface RecursoDeProjeto {
  id: string;
  userId: string;
  roleInProject: string | null;
  allocationPct: number;
  startAt: number | null;
  finishAt: number | null;
}

export interface Projeto extends LinhaDeProjeto {
  tasks: TarefaDeProjeto[];
  milestones: MarcoDeProjeto[];
  risks: RiscoDeProjeto[];
  resources: RecursoDeProjeto[];
}

export interface PaginaDeProjetos {
  items: LinhaDeProjeto[];
  nextCursor: string | null;
}

// ── leitura ──────────────────────────────────────────────────────────────────

export const listarProjetos = (params?: {
  status?: StatusDeProjeto;
  q?: string;
  cursor?: string;
}) => {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.q) qs.set('q', params.q);
  if (params?.cursor) qs.set('cursor', params.cursor);
  const sufixo = qs.toString() ? `?${qs}` : '';
  return get<PaginaDeProjetos>(`${BASE}${sufixo}`);
};

export const obterProjeto = (id: string) => get<Projeto>(`${BASE}/${id}`);

// ── escrita ──────────────────────────────────────────────────────────────────

export interface NovoProjeto {
  name: string;
  description?: string;
  accountId?: string;
  managerId?: string;
  teamId?: string;
  startAt?: number;
  finishAt?: number;
}

export const criarProjeto = (corpo: NovoProjeto) => post<LinhaDeProjeto>(BASE, corpo);

export const atualizarProjeto = (id: string, corpo: Partial<NovoProjeto & { status: StatusDeProjeto }>) =>
  patch<LinhaDeProjeto>(`${BASE}/${id}`, corpo);

export const excluirProjeto = (id: string) => del(`${BASE}/${id}`);

// ── tarefas ──────────────────────────────────────────────────────────────────

export const criarTarefa = (
  projetoId: string,
  corpo: { name: string; parentId?: string; durationMinutes?: number; isMilestone?: boolean },
) => post<TarefaDeProjeto>(`${BASE}/${projetoId}/tasks`, corpo);

export const atualizarTarefa = (
  projetoId: string,
  tarefaId: string,
  corpo: Partial<{
    name: string;
    description: string | null;
    durationMinutes: number;
    progress: number;
    assigneeId: string | null;
    teamId: string | null;
    constraintType: string;
    constraintDate: number | null;
    deadline: number | null;
    boardSectionId: string;
    recurrence: string;
    recurrenceInterval: number;
  }>,
) => patch<TarefaDeProjeto>(`${BASE}/${projetoId}/tasks/${tarefaId}`, corpo);

export const removerTarefa = (projetoId: string, tarefaId: string) =>
  del(`${BASE}/${projetoId}/tasks/${tarefaId}`);

export const moverTarefa = (
  projetoId: string,
  tarefaId: string,
  corpo: { parentId?: string | null; position: number },
) => patch<TarefaDeProjeto>(`${BASE}/${projetoId}/tasks/${tarefaId}/move`, corpo);

export const vincularChamado = (tarefaId: string, ticketId: string | null) =>
  patch<TarefaDeProjeto>(`${BASE}/tasks/${tarefaId}/ticket`, { ticketId });

export const adicionarDependencia = (
  projetoId: string,
  predecessorId: string,
  corpo: { successorId: string; kind?: 'FS' | 'SS' | 'FF' | 'SF'; lagMinutes?: number },
) => post<DependenciaDeTarefa>(`${BASE}/${projetoId}/tasks/${predecessorId}/dependencies`, corpo);

export const removerDependencia = (projetoId: string, depId: string) =>
  del(`${BASE}/${projetoId}/dependencies/${depId}`);

export const obterCronograma = (projetoId: string) =>
  get<ResultadoCronograma>(`${BASE}/${projetoId}/schedule`);

// ── marcos ───────────────────────────────────────────────────────────────────

export const criarMarco = (projetoId: string, corpo: { name: string; dueAt: number }) =>
  post<MarcoDeProjeto>(`${BASE}/${projetoId}/milestones`, corpo);

// ── riscos ───────────────────────────────────────────────────────────────────

export const criarRisco = (
  projetoId: string,
  corpo: { title: string; severity?: string; description?: string; mitigation?: string },
) => post<RiscoDeProjeto>(`${BASE}/${projetoId}/risks`, corpo);

export const atualizarRisco = (
  projetoId: string,
  riscoId: string,
  corpo: Partial<{ title: string; description: string; severity: string; status: string; mitigation: string }>,
) => patch<RiscoDeProjeto>(`${BASE}/${projetoId}/risks/${riscoId}`, corpo);

// ── recursos ─────────────────────────────────────────────────────────────────

export const alocarRecurso = (
  projetoId: string,
  corpo: { userId: string; allocationPct?: number; roleInProject?: string },
) => post<RecursoDeProjeto>(`${BASE}/${projetoId}/resources`, corpo);

export const desalocarRecurso = (projetoId: string, userId: string) =>
  del(`${BASE}/${projetoId}/resources/${userId}`);

// ── comentários de tarefa ─────────────────────────────────────────────────────

export const listarComentariosDeTarefa = (projetoId: string, tarefaId: string) =>
  get<ComentarioDeTarefa[]>(`${BASE}/${projetoId}/tasks/${tarefaId}/comments`);

export const adicionarComentarioDeTarefa = (projetoId: string, tarefaId: string, body: string) =>
  post<ComentarioDeTarefa>(`${BASE}/${projetoId}/tasks/${tarefaId}/comments`, { body });

// ── checklist de tarefa ───────────────────────────────────────────────────────

export const listarChecklist = (projetoId: string, tarefaId: string) =>
  get<ChecklistItem[]>(`${BASE}/${projetoId}/tasks/${tarefaId}/checklist`);

export const adicionarItemChecklist = (projetoId: string, tarefaId: string, body: string) =>
  post<ChecklistItem>(`${BASE}/${projetoId}/tasks/${tarefaId}/checklist`, { body });

export const atualizarItemChecklist = (
  projetoId: string,
  tarefaId: string,
  itemId: string,
  dados: { done?: boolean; body?: string },
) => patch<ChecklistItem>(`${BASE}/${projetoId}/tasks/${tarefaId}/checklist/${itemId}`, dados);

export const removerItemChecklist = (projetoId: string, tarefaId: string, itemId: string) =>
  del(`${BASE}/${projetoId}/tasks/${tarefaId}/checklist/${itemId}`);

// ── minhas tarefas ────────────────────────────────────────────────────────────

export const minhasTarefas = () =>
  get<TarefaMinhaAtividade[]>(`${BASE}/my-tasks`);

// ── membros (para @menções) ───────────────────────────────────────────────────

export interface MembroParaMencao {
  id: string;
  name: string;
  displayName: string | null;
}

export const buscarMembros = (q: string) =>
  get<MembroParaMencao[]>(`${BASE}/members?q=${encodeURIComponent(q)}`);

// ── workload ─────────────────────────────────────────────────────────────────

export interface TarefaDeWorkload {
  id: string;
  name: string;
  progress: number;
  startAt: number | null;
  finishAt: number | null;
  isCritical: boolean;
  assigneeId: string | null;
  project: { id: string; number: string; name: string };
}

export interface MembroWorkload {
  user: { id: string; name: string; displayName: string | null };
  total: number;
  done: number;
  overdue: number;
  critical: number;
  tasks: TarefaDeWorkload[];
}

export const obterWorkload = () => get<MembroWorkload[]>(`${BASE}/workload`);

// ── atividade de tarefa ───────────────────────────────────────────────────────

export interface AtividadeDeTarefa {
  id: string;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
  user: { id: string; name: string; displayName: string | null } | null;
}

export const listarAtividadeDeTarefa = (projetoId: string, tarefaId: string) =>
  get<AtividadeDeTarefa[]>(`${BASE}/${projetoId}/tasks/${tarefaId}/activity`);

// ── burndown ─────────────────────────────────────────────────────────────────

export interface PontoBurndown {
  date: number;
  remaining: number;
  ideal: number;
}

export interface ResultadoBurndown {
  points: PontoBurndown[];
  total: number;
  done: number;
}

export const obterBurndown = (projetoId: string) =>
  get<ResultadoBurndown>(`${BASE}/${projetoId}/burndown`);

// ── seções do board ───────────────────────────────────────────────────────────

export interface BoardSection {
  id: string;
  name: string;
  position: number;
  isDone: boolean;
}

export const listarSecoesDoProjeto = (projetoId: string) =>
  get<BoardSection[]>(`${BASE}/${projetoId}/board-sections`);

export const criarSecao = (projetoId: string, name: string) =>
  post<BoardSection>(`${BASE}/${projetoId}/board-sections`, { name });

export const renomearSecao = (projetoId: string, sectionId: string, name: string) =>
  patch<BoardSection>(`${BASE}/${projetoId}/board-sections/${sectionId}`, { name });

export const excluirSecao = (projetoId: string, sectionId: string) =>
  api<{ ok: boolean }>(`${BASE}/${projetoId}/board-sections/${sectionId}`, { method: 'DELETE' });

// ── colaboradores de tarefa ────────────────────────────────────────────────────

export interface ColaboradorDeTarefa {
  id: string;
  userId: string;
  addedAt: string;
  user: { id: string; name: string; displayName: string | null; email: string };
}

export const listarColaboradores = (projetoId: string, tarefaId: string) =>
  get<ColaboradorDeTarefa[]>(`${BASE}/${projetoId}/tasks/${tarefaId}/collaborators`);

export const adicionarColaborador = (projetoId: string, tarefaId: string, userId?: string) =>
  post<ColaboradorDeTarefa>(`${BASE}/${projetoId}/tasks/${tarefaId}/collaborators`, userId ? { userId } : {});

export const removerColaborador = (projetoId: string, tarefaId: string, userId: string) =>
  api<void>(`${BASE}/${projetoId}/tasks/${tarefaId}/collaborators/${userId}`, { method: 'DELETE' });

// ── saúde do projeto ─────────────────────────────────────────────────────────

export interface SaudeDoProjeto {
  tasksTotal: number;
  tasksDone: number;
  tasksOverdue: number;
  criticalOpen: number;
  progressPct: number;
  topRisk: { title: string; severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' } | null;
  status: 'ON_TRACK' | 'AT_RISK' | 'DELAYED';
}

export const obterSaude = (projetoId: string) =>
  get<SaudeDoProjeto>(`${BASE}/${projetoId}/health`);

// ── mensagens de projeto ────────────────────────────────────────────────────

export interface RespostaMensagem {
  id: string;
  authorId: string | null;
  author: { id: string; name: string; displayName: string | null } | null;
  body: string;
  createdAt: string;
}

export interface MensagemDeProjeto {
  id: string;
  authorId: string | null;
  author: { id: string; name: string; displayName: string | null } | null;
  subject: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  replies: RespostaMensagem[];
}

export const listarMensagens = (projetoId: string) =>
  get<MensagemDeProjeto[]>(`${BASE}/${projetoId}/messages`);

export const criarMensagem = (projetoId: string, dto: { subject: string; body: string }) =>
  post<MensagemDeProjeto>(`${BASE}/${projetoId}/messages`, dto);

export const responderMensagem = (projetoId: string, msgId: string, body: string) =>
  post<RespostaMensagem>(`${BASE}/${projetoId}/messages/${msgId}/replies`, { body });

// ── time tracking ─────────────────────────────────────────────────────────────

export interface RegistroDeHoras {
  id: string;
  minutes: number;
  description?: string | null;
  loggedAt: string;
  user: { id: string; name: string; displayName?: string | null };
}

export interface ListaDeHoras {
  logs: RegistroDeHoras[];
  totalMinutes: number;
}

export const listarHoras = (projetoId: string, tarefaId: string) =>
  get<ListaDeHoras>(`${BASE}/${projetoId}/tasks/${tarefaId}/time-logs`);

export const registrarHoras = (projetoId: string, tarefaId: string, minutes: number, description?: string) =>
  post<RegistroDeHoras>(`${BASE}/${projetoId}/tasks/${tarefaId}/time-logs`, { minutes, description });

export const excluirRegistroHoras = (projetoId: string, tarefaId: string, logId: string) =>
  del(`${BASE}/${projetoId}/tasks/${tarefaId}/time-logs/${logId}`);

// ── templates de projeto ─────────────────────────────────────────────────────

export interface TemplateDeProjeto {
  id: string;
  name: string;
  description: string;
  tasks: Array<{ name: string; durationMinutes: number; parentIndex?: number }>;
}

// ── custom fields ─────────────────────────────────────────────────────────────

export interface CampoPersonalizado {
  id: string;
  fieldName: string;
  fieldValue: string;
  createdAt: string;
}

export const listarCampos = (projetoId: string, tarefaId: string) =>
  get<CampoPersonalizado[]>(`${BASE}/${projetoId}/tasks/${tarefaId}/custom-fields`);

export const criarCampo = (projetoId: string, tarefaId: string, fieldName: string, fieldValue: string) =>
  post<CampoPersonalizado>(`${BASE}/${projetoId}/tasks/${tarefaId}/custom-fields`, { fieldName, fieldValue });

export const atualizarCampo = (projetoId: string, tarefaId: string, fieldId: string, fieldValue: string) =>
  patch<CampoPersonalizado>(`${BASE}/${projetoId}/tasks/${tarefaId}/custom-fields/${fieldId}`, { fieldValue });

export const excluirCampo = (projetoId: string, tarefaId: string, fieldId: string) =>
  api<{ ok: boolean }>(`${BASE}/${projetoId}/tasks/${tarefaId}/custom-fields/${fieldId}`, { method: 'DELETE' });

export const TEMPLATES_DE_PROJETO: TemplateDeProjeto[] = [
  {
    id: 'implantacao-software',
    name: 'Implantação de Software',
    description: 'Levantamento, configuração, testes e go-live',
    tasks: [
      { name: 'Levantamento de requisitos', durationMinutes: 2400 },
      { name: 'Configuração do ambiente', durationMinutes: 480 },
      { name: 'Migração de dados', durationMinutes: 960 },
      { name: 'Testes de aceitação', durationMinutes: 1440 },
      { name: 'Treinamento de usuários', durationMinutes: 480 },
      { name: 'Go-live', durationMinutes: 240 },
    ],
  },
  {
    id: 'evento-corporativo',
    name: 'Evento Corporativo',
    description: 'Planejamento e execução de evento interno',
    tasks: [
      { name: 'Definir data e local', durationMinutes: 480 },
      { name: 'Convites e confirmações', durationMinutes: 960 },
      { name: 'Contratar fornecedores', durationMinutes: 1440 },
      { name: 'Preparar material', durationMinutes: 960 },
      { name: 'Execução do evento', durationMinutes: 480 },
      { name: 'Relatório pós-evento', durationMinutes: 240 },
    ],
  },
  {
    id: 'sprint-desenvolvimento',
    name: 'Sprint de Desenvolvimento',
    description: 'Sprint ágil de 2 semanas',
    tasks: [
      { name: 'Planning', durationMinutes: 240 },
      { name: 'Desenvolvimento', durationMinutes: 4800 },
      { name: 'Code review', durationMinutes: 960 },
      { name: 'Testes QA', durationMinutes: 960 },
      { name: 'Deploy', durationMinutes: 240 },
      { name: 'Retrospectiva', durationMinutes: 120 },
    ],
  },
];
